import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import {
  ConsultationBillingStatus,
  ConsultationDetail,
  ConsultationStatus,
} from '@/types';

const STATUS_TONE: Record<ConsultationStatus, string> = {
  booking_requested: 'bg-slate-100 text-slate-700',
  awaiting_consultant: 'bg-amber-100 text-amber-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  denied: 'bg-red-100 text-red-700',
  referred: 'bg-violet-100 text-violet-700',
  pending_reschedule_confirmation: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-200 text-slate-700',
  no_show: 'bg-red-100 text-red-700',
  rescheduled: 'bg-violet-100 text-violet-700',
};

const BILLING_TONE: Record<ConsultationBillingStatus, string> = {
  not_requested: 'bg-slate-100 text-slate-600',
  pending_payment: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-slate-200 text-slate-700',
  refunded: 'bg-red-100 text-red-700',
};

function formatNaira(value: string | null) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || !value) return '—';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function ConsultationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<ConsultationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ConsultationDetail>(
        `/back-office/consultations/${id}`
      );
      setDetail(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load consultation'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="px-8 py-6 text-slate-500">Loading…</div>;

  if (error || !detail) {
    return (
      <div className="px-8 py-6">
        <Link
          to="/consultations"
          className="text-sm text-brand-600 hover:underline"
        >
          ← Back to consultations
        </Link>
        <div className="mt-4 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Consultation not found'}
        </div>
      </div>
    );
  }

  const { appointment, patient, consultant } = detail;

  return (
    <div className="px-8 py-6">
      <Link
        to="/consultations"
        className="text-sm text-brand-600 hover:underline"
      >
        ← Back to consultations
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">
            Consultation #{appointment.id}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span
              className={clsx(
                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_TONE[appointment.status]
              )}
            >
              {appointment.status.replace(/_/g, ' ')}
            </span>
            <span
              className={clsx(
                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                BILLING_TONE[appointment.billing_status]
              )}
            >
              {appointment.billing_status.replace(/_/g, ' ')}
            </span>
            <span>·</span>
            <span className="capitalize">
              {appointment.consultation_type}
            </span>
            <span>·</span>
            <span>{appointment.duration_minutes} min</span>
            <span>·</span>
            <span>{formatNaira(appointment.consultation_fee)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Schedule">
            <DetailRow
              label="Scheduled start"
              value={formatDate(appointment.scheduled_start_time)}
            />
            <DetailRow
              label="Scheduled end"
              value={formatDate(appointment.scheduled_end_time)}
            />
            <DetailRow
              label="Confirmed"
              value={formatDate(appointment.confirmed_at)}
            />
            <DetailRow
              label="Started"
              value={formatDate(appointment.started_at)}
            />
            <DetailRow
              label="Completed"
              value={formatDate(appointment.completed_at)}
            />
            {appointment.cancelled_at && (
              <DetailRow
                label="Cancelled"
                value={
                  <>
                    {formatDate(appointment.cancelled_at)}
                    {appointment.cancelled_by && (
                      <span className="ml-1 text-xs text-slate-500">
                        by {appointment.cancelled_by}
                      </span>
                    )}
                  </>
                }
              />
            )}
          </Section>

          {(appointment.reason || appointment.notes) && (
            <Section title="Patient context">
              {appointment.reason && (
                <div className="mb-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Reason
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {appointment.reason}
                  </p>
                </div>
              )}
              {appointment.notes && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Notes
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {appointment.notes}
                  </p>
                </div>
              )}
            </Section>
          )}

          {(appointment.diagnosis ||
            appointment.prescription ||
            appointment.follow_up_notes) && (
            <Section title="Clinical record">
              {appointment.diagnosis && (
                <div className="mb-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Diagnosis
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {appointment.diagnosis}
                  </p>
                </div>
              )}
              {appointment.prescription && (
                <div className="mb-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Prescription
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {appointment.prescription}
                  </p>
                </div>
              )}
              {appointment.follow_up_notes && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Follow-up
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {appointment.follow_up_notes}
                  </p>
                  {appointment.follow_up_date && (
                    <div className="mt-0.5 text-xs text-slate-500">
                      On {appointment.follow_up_date}
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          <Section title="Billing">
            <DetailRow
              label="Reference"
              value={
                appointment.payment_reference ? (
                  <span className="font-mono text-xs">
                    {appointment.payment_reference}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow
              label="Fee"
              value={formatNaira(appointment.consultation_fee)}
            />
            <DetailRow
              label="Payment status"
              value={appointment.payment_status ?? '—'}
            />
            <DetailRow
              label="Billing status"
              value={appointment.billing_status.replace(/_/g, ' ')}
            />
          </Section>
        </div>

        <div className="space-y-6 lg:col-span-1">
          <Section title="Patient">
            {patient ? (
              <>
                <DetailRow
                  label="Name"
                  value={
                    <Link
                      to={`/users/${patient.id}`}
                      className="text-brand-600 hover:underline"
                    >
                      {patient.first_name} {patient.last_name}
                    </Link>
                  }
                />
                <DetailRow label="Email" value={patient.email} />
                <DetailRow label="Phone" value={patient.phone ?? '—'} />
              </>
            ) : (
              <div className="text-sm text-slate-500">No patient linked.</div>
            )}
          </Section>

          <Section title="Consultant">
            {consultant ? (
              <>
                <DetailRow
                  label="Name"
                  value={
                    <Link
                      to={`/users/${consultant.user_id}`}
                      className="text-brand-600 hover:underline"
                    >
                      {consultant.first_name} {consultant.last_name}
                    </Link>
                  }
                />
                <DetailRow label="Email" value={consultant.email} />
              </>
            ) : (
              <div className="text-sm text-slate-500">No consultant linked.</div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{value}</span>
    </div>
  );
}
